import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowSquareOut, Copy, PaperPlaneTilt } from "@phosphor-icons/react";

const ENGINES = [
  {
    name: "Google Search Console",
    logo: "🔍",
    url: "https://search.google.com/search-console",
    hint: "Add your property → Sitemaps → paste the sitemap URL below. Reaches Google + partners.",
    action: "Open Search Console",
  },
  {
    name: "Bing Webmaster Tools",
    logo: "🟦",
    url: "https://www.bing.com/webmasters",
    hint: "Import your Google Search Console properties with one click, or add manually.",
    action: "Open Bing Webmaster",
  },
  {
    name: "Yandex Webmaster",
    logo: "🟥",
    url: "https://webmaster.yandex.com",
    hint: "For Russian & CIS traffic. Also submits to IndexNow automatically.",
    action: "Open Yandex Webmaster",
  },
  {
    name: "Naver Search Advisor",
    logo: "🟢",
    url: "https://searchadvisor.naver.com",
    hint: "For Korean traffic. Free account required.",
    action: "Open Naver Advisor",
  },
  {
    name: "Baidu Ziyuan (百度)",
    logo: "🟨",
    url: "https://ziyuan.baidu.com/site/index",
    hint: "For Chinese traffic. Requires Chinese phone number for verification.",
    action: "Open Baidu Ziyuan",
  },
  {
    name: "Seznam.cz",
    logo: "⚫",
    url: "https://napoveda.sklik.cz/seznam-hledani",
    hint: "Czech Republic's largest search engine. Already covered by IndexNow.",
    action: "Learn more",
  },
  {
    name: "IndexNow (Bing + Yandex + Seznam + Naver)",
    logo: "⚡",
    url: "https://www.indexnow.org/",
    hint: "Instant indexing protocol. Auto-triggered when you publish content — already wired up.",
    action: "About IndexNow",
  },
];

function CopyRow({ label, value, testid }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  return (
    <div className="flex items-center gap-2 border border-[#E5E0D8] rounded-lg px-3 py-2 bg-white">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-[#5C5A56]">{label}</p>
        <p className="text-xs font-mono text-[#2C2B29] truncate" data-testid={`${testid}-value`}>{value || "—"}</p>
      </div>
      <button
        onClick={copy}
        data-testid={`${testid}-copy`}
        className="shrink-0 text-[#5B7B53] hover:text-[#D17A58] p-1.5 rounded-md hover:bg-[#EADDC8]/40"
        title="Copy to clipboard"
      >
        <Copy size={14} weight="bold" />
      </button>
    </div>
  );
}

function slug(s) {
  return s
    .toLowerCase()
    .replace(/[.()+]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function AdminSEO() {
  const [status, setStatus] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/indexnow/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);

  const publicUrl = status?.public_site_url || "";
  const sitemapUrl = publicUrl ? `${publicUrl}/api/sitemap.xml` : "";
  const robotsUrl = publicUrl ? `${publicUrl}/robots.txt` : "";

  const submitAll = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/indexnow/submit-all");
      setLastResult(data);
      if (data.submitted) toast.success(`Submitted ${data.total_urls} URLs`);
      else toast.warning(data.reason || "Submission skipped");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitBulk = async () => {
    const urls = bulkText.split(/\s+/).map((u) => u.trim()).filter(Boolean);
    if (urls.length === 0) {
      toast.error("Paste at least one URL");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/indexnow/submit", { urls });
      setLastResult({ ...data, total_urls: urls.length });
      if (data.submitted) {
        toast.success(`Submitted ${urls.length} URL(s)`);
        setBulkText("");
      } else {
        toast.warning(data.reason || "Submission rejected");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Grow traffic</p>
      <h1 className="font-display text-4xl text-[#2C2B29] mb-3">Search engine submission</h1>
      <p className="text-sm text-[#5C5A56] max-w-2xl mb-10">
        One-click bulk submission via IndexNow (reaches Bing, Yandex, Seznam, Naver instantly), plus direct links & pre-filled URLs for every other major search engine you'll need to add manually.
      </p>

      {/* Site URLs */}
      <div className="grid md:grid-cols-2 gap-3 mb-10">
        <CopyRow label="Sitemap URL" value={sitemapUrl} testid="sitemap" />
        <CopyRow label="Robots.txt URL" value={robotsUrl} testid="robots" />
      </div>

      {/* IndexNow section */}
      <div className="border border-[#E5E0D8] bg-white rounded-2xl p-6 mb-10">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h2 className="font-display text-2xl text-[#2C2B29]">IndexNow — instant bulk indexing</h2>
            <p className="text-xs text-[#5C5A56] mt-1 max-w-lg">
              {status?.configured ? "✓ Configured" : "Not configured"} · Notifies Bing, Yandex, Seznam, Naver, Wolfram Alpha and DuckDuckGo (via Bing).
            </p>
          </div>
          <button
            data-testid="seo-submit-all"
            onClick={submitAll}
            disabled={submitting || !status?.configured}
            className="hh-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <PaperPlaneTilt size={14} weight="bold" /> Submit all site URLs
          </button>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">
            Bulk submit specific URLs (one per line — paste any URLs you've just changed)
          </label>
          <textarea
            data-testid="seo-bulk-urls"
            rows={5}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={`${publicUrl}/product/abc123\n${publicUrl}/blog/xyz456`}
            className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm font-mono"
          />
          <button
            data-testid="seo-submit-bulk"
            onClick={submitBulk}
            disabled={submitting || !status?.configured}
            className="mt-3 rounded-full px-5 py-2 text-sm font-semibold border border-[#2C2B29] text-[#2C2B29] hover:bg-[#2C2B29] hover:text-white disabled:opacity-50 transition-colors"
          >
            {submitting ? "Submitting..." : "Submit pasted URLs"}
          </button>
        </div>

        {lastResult && (() => {
          const sc = lastResult.status_code;
          const ok = sc && sc >= 200 && sc < 300;
          return (
            <p className={`text-xs mt-4 ${ok ? "text-[#5B7B53]" : "text-[#D17A58]"}`}>
              Last submit: {lastResult.total_urls} URLs · HTTP {sc || "—"} {ok ? "✓ accepted" : "· not accepted"}
            </p>
          );
        })()}
      </div>

      {/* Manual engines */}
      <div>
        <h2 className="font-display text-2xl text-[#2C2B29] mb-2">Manual submission</h2>
        <p className="text-sm text-[#5C5A56] mb-6">
          Google no longer accepts automatic sitemap pings — you'll need to add your site once via Search Console. Others below are optional but useful for reaching regional audiences.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {ENGINES.map((e) => (
            <div key={e.name} data-testid={`engine-${slug(e.name)}`} className="border border-[#E5E0D8] bg-white rounded-xl p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl" aria-hidden="true">{e.logo}</span>
                <h3 className="font-display text-lg text-[#2C2B29]">{e.name}</h3>
              </div>
              <p className="text-xs text-[#5C5A56] flex-1 mb-4">{e.hint}</p>
              <a
                href={e.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5B7B53] hover:text-[#D17A58] self-start"
              >
                {e.action} <ArrowSquareOut size={14} weight="bold" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
